import { CrownIcon, CrossedLimitIcon, ImprovedIcon, NotImprovedIcon, ZeroIcon } from 'assests/GmsIcons';
import React, { useContext } from 'react';
import { ABContext } from '../../contexts/ABContext';
import { TABResult } from '../../types/ab';
import CustomChip from '../../components/CustomChip';
import { NORMALIZED } from '../../utils/Constants';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import StatusIndicator from '../../components/StatusIndicator';
import ResultLegend from '../../components/ResultLegend';
import Table from 'Components/DesignSystem/Table/Table';
import Radio from 'Components/DesignSystem/Radio/Radio';
import { TableColumn } from 'types/DesignSystem/Table';

/**
 * The per-model results grid (`okf/design-system/e2e.md` § 159), on the DS `Table`.
 *
 * **`getModelTypeList()` is untouched** — control sorts first, then variants are
 * numbered in their original order, and the display names stay `Control: {name}` /
 * `Variant N: {name}`. That numbering is derived from position AFTER the sort, so any
 * change to the sort silently renumbers the variants; it is left exactly as it was.
 *
 * `checkBox` is what turns this from the read-only results view into the model picker
 * inside `ABPromoteModal`, adding the radio column. The name is kept (two call sites and
 * the tests use it) even though the control is a radio, not a checkbox.
 *
 * **The radio is a real `Radio` in a named group now.** They were bare
 * `<input type="radio" name={`radio-group-${i}`}>` — a DIFFERENT name per row, which
 * means they were not a group at all: arrow keys did nothing and, more importantly,
 * nothing enforced single-selection at the DOM level. It held together only because
 * `checked` is driven from `selectedModelId`. Each also had no label, so a screen reader
 * announced an unnamed radio and the model name arrived separately in the next cell.
 *
 * > The empty spacer `<tr>` of five `tw-h-4` cells that used to sit above the first row
 * > is gone — it was a shim for the header's line height, and it did not even match the
 * > column count (five cells for seven or eight columns), so the browser was silently
 * > patching the row.
 */
const ResultTable = ({ checkBox }: { checkBox?: boolean }) => {
    const abContext = useContext(ABContext);
    const { testData, selectedModelId, setSelectedModelId } = abContext

    const getModelTypeList = () => {
        let index = 1;
        const sortedData = (testData?.result || []).sort((a: TABResult, b: TABResult) => {
            if (a.is_control && !b.is_control) return -1;
            if (!a.is_control && b.is_control) return 1;
            return 0;
        });

        const returnData = sortedData.map((x: TABResult) => {
            let name = x?.name ?? '';
            if (x?.is_control) {
                name = `Control: ${x?.name ?? ''}`;
            } else {
                name = `Variant ${index}: ${x?.name ?? ''}`;
                index += 1;
            }
            return { ...x, name };
        });

        return returnData;
    }

    const getIcon = (status: string, rank?: number) => {
        if (!status) return null;

        const config = {
            won: {
                icon: <CrownIcon />,
                text: ''
            },
            crossed_limit: {
                icon: <CrossedLimitIcon />,
                text: ''
            },
            improved: {
                icon: <ImprovedIcon />,
                text: ''
            },
            not_improved: {
                icon: <NotImprovedIcon />,
                text: ''
            },
            zero: {
                icon: <ZeroIcon />,
                text: ''
            }
        };

        const result = config[status as keyof typeof config];

        if (!result) return null;

        return (
            <div className="tw-flex tw-items-center tw-gap-1">
                {result.icon}
                <span className="tw-text-sm tw-text-ink">{result.text}</span>
            </div>
        );
    };

    const rows = getModelTypeList();

    const columns: TableColumn<TABResult>[] = [
        ...(checkBox ? [{
            id: 'select',
            header: '',
            accessor: () => null,
            width: '48px',
            render: (_v: any, res: TABResult) => (
                <Radio
                    name="ab-promote-model"
                    /* The model name is the next cell over, so the visible label would
                       duplicate it — but the radio still needs a name of its own, or it
                       announces as an unnamed option. `sr-only` inside the `<label>`
                       keeps ONE name source and keeps the label a click target. */
                    label={<span className="tw-sr-only">{res?.name ?? ''}</span>}
                    value={res?.id}
                    checked={selectedModelId === res?.id}
                    onChange={() => setSelectedModelId(res?.id)}
                    data-testid={`result-resulttable-input-${res?.id}`}
                />
            ),
        } satisfies TableColumn<TABResult>] : []),
        {
            id: 'models',
            header: 'Models',
            accessor: (r) => r?.name ?? '',
            width: '26%',
            render: (_v, res) => (
                <div className='tw-flex tw-items-center tw-gap-2'>
                    <span>{res?.name}</span>
                    {res?.is_promoted ? <CustomChip status="Promoted" data-testid={`ab-result-promoted-${res?.id}`} /> : null}
                </div>
            ),
        },
        {
            id: 'performance',
            header: 'Value',
            accessor: (r) => r?.performance?.toFixed(2) ?? '-',
            cellType: 'numerical',
        },
        {
            id: 'session_count',
            header: 'Session count',
            accessor: (r) => r?.session_count ?? '0',
            cellType: 'numerical',
        },
        {
            id: 'performance_per_session',
            header: 'Value Per session',
            accessor: (r) => r?.performance_per_session?.toFixed(2) ?? '0.00',
            cellType: 'numerical',
        },
        {
            id: 'normalised_performance',
            header: 'Normalized Value',
            accessor: (r) => r?.normalised_performance?.toFixed(2) ?? '-',
            cellType: 'numerical',
            /* In the header, where it describes a column — `TableColumn.headerInfo`
               (§ 164). Only in the READ-ONLY view: inside the promote modal the same
               explanation is already above the table, and `checkBox` is how this
               component knows which context it is in. */
            headerInfo: !checkBox
                ? <InfoTooltip message={NORMALIZED} label="About Normalized Value" data-testid="ab-result-normalised-info" />
                : undefined,
            render: (value, _res, rowIndex) => (
                <span data-testid={`ab-result-normalised-${rowIndex}`}>{value}</span>
            ),
        },
        {
            id: 'improvement',
            header: 'Improvement',
            accessor: (r) => (r?.is_control ? 'Baseline' : r?.improvement != null ? `${r.improvement?.toFixed(2)} %` : '-'),
            cellType: 'numerical',
        },
        {
            id: 'result_status',
            header: 'Result',
            accessor: () => null,
            width: '10%',
            headerInfo: !checkBox
                ? <InfoTooltip message={<ResultLegend />} label="About the Result column" data-testid="ab-result-legend-info" />
                : undefined,
            render: (_v, res) => (
                <div className="tw-flex tw-items-center">
                    {!res?.is_control && getIcon(res?.result_status, res?.rank)}
                </div>
            ),
        },
    ];

    return (
        <div>
            <div className='tw-w-fit tw-ms-2'>
                <StatusIndicator
                    message={testData?.metric ?? ''}
                    placeholder={`Metric:`}
                    data-testid="ab-result-metric"
                />
            </div>
            <div className="tw-m-2">
                <Table
                    columns={columns}
                    data={rows}
                    rowKey="id"
                    striped
                    data-testid="ab-result-table"
                />
            </div>
        </div>
    );
};

export default ResultTable;